import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ColDef, GridOptions, GridApi } from 'ag-grid-community';
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { chiefsOrderLevel } from '../../../models/comodel/chiefsOrderLevel.model';
import { ChiefsOrderService } from '../../../services/chiefsOrder.service';
import { ButtonRendererComponent } from "../../../common/components/ag-grid/button-renderer.component";
import { DomainDefinitionService } from '../../../builder/services/domain-definition.service';
import { GroupService } from '../../services/group.service';
import { Observable, Subscription, Subject } from 'rxjs';
import { Group } from '../../models/group.model';
import { DomainValue } from '../../../models/domain.model';
import { LunaDomain } from '../../../builder/models/domain.model';
import { takeUntil, takeWhile, finalize } from 'rxjs/operators';;
import { LoaderService } from '../../../common/services/loader.service';
import { SubHeader } from '../../../core/models/sub-header.model';
import { SubHeaderService } from '../../../core/services/sub-header.service';


@Component({
    selector: 'co-level-config',
    templateUrl: './co-level-config.component.html',
    styleUrls: ['./co-level-config.component.scss'],
    standalone: false
})
export class CoLevelConfigComponent implements OnInit, OnDestroy  {
 
    coLevels: chiefsOrderLevel[] = [];
    newCoLevel: chiefsOrderLevel;
    activeCategories: DomainValue[];
    activeGroups: Group[] = [];    
    sub: Subscription[] = [];    
    gridApi: GridApi;
    editType;
    editingRowIndex;
    levelNumbers: number[] = [];  
    uniqueLevel: boolean = false;
    hideGroupAssignment: boolean = false;

    levelConfigForm: FormGroup = new FormGroup({
        category: new FormControl('', Validators.required),
        level: new FormControl('', [Validators.required, Validators.min(1)]),
        assignTo: new FormControl(''),
        returnTo: new FormControl(''),
        notification: new FormControl('', Validators.maxLength(100)),
        mode: new FormControl(null, Validators.required),
        canReject: new FormControl(false),
        canSelectAssignToGroup: new FormControl(false)
    });

    colCategory: ColDef = {
        headerName: 'Category',
        headerTooltip: 'Category',
        field: 'categoryCode',
        tooltipField: 'categoryCode',
        minWidth: 150,
        maxWidth: 200,
        editable: false
    };

    colLevelId: ColDef = {         
        field: 'levelId',         
        editable: false,
        hide:true
    };

    colLevel: ColDef = {
        headerName: 'Level Number',
        headerTooltip: 'Level Number',
        field: 'levelNumber',
        tooltipField: 'level',
        maxWidth: 200,
        editable:false
    };    

    colAssignTo: ColDef = {
        headerName: 'Assign To',
        headerTooltip: 'Assign To',
        field: 'assignToGroup',
        tooltipField: 'assignToGroup',
        maxWidth: 200,
        editable: false
    };

    colReturnTo: ColDef = {
        headerName: 'Return To',
        headerTooltip: 'Return To',
        field: 'returnToGroup',
        tooltipField: 'returnToGroup',
        maxWidth: 300,
        editable: false
    };

    colMode: ColDef = {
        headerName: 'Mode',
        headerTooltip: 'Mode',
        field: 'mode',
        tooltipField: 'mode',
        minWidth: 125,
        maxWidth: 150,
        editable: false    
    };

    colNotification: ColDef = {
        headerName: 'Notification',
        headerTooltip: 'Notification',
        field: 'notification',
        tooltipField: 'notification',
        maxWidth: 250,
        editable: false        
    };  
   
    colCanReject: ColDef = {
        headerName: 'Can Reject',
        headerTooltip: 'Can Reject',
        field: 'canReject',
        valueGetter: this.canCancelFormatter(this),
        maxWidth: 200
    };

    colcanSelectAssignToGroup: ColDef = {
        headerName: 'Can Assign At Review',
        headerTooltip: 'Can Assign At Review',
        field: 'canSelectAssignToGroup',
        valueGetter: this.canAssignGroupAtReview(this),
        maxWidth: 300
    };

    colEdit: ColDef = {
        headerName: '',
        headerTooltip: '',
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: ButtonRendererComponent,
		cellRendererParams: {
		    action: (obj: any) => this.deleteLevel(obj),
			buttonText: "Remove"
		},
        filter: false,
        sortable: false,
        suppressFiltersToolPanel: true
    };

    columnDefs = [this.colCategory, this.colLevel, this.colcanSelectAssignToGroup, this.colAssignTo, this.colReturnTo, this.colMode, this.colNotification, this.colCanReject, this.colEdit];

    gridOptions: GridOptions = {
        columnDefs: this.columnDefs,
        headerHeight: 50,
        defaultColDef: { filter: true, sortable: true, resizable: true, headerClass: "wrapped" } as ColDef,
        enableBrowserTooltips: false,
        tooltipTrigger: 'focus',
        onGridReady: (params) => {
            this.gridApi = params.api;
			this.gridApi.sizeColumnsToFit();
        },
    };

    subHeader: SubHeader = new SubHeader({ title: "Chief's Order Level Configuration" });

    constructor(public srv: ChiefsOrderService, public domainService: DomainDefinitionService, private _formatter: GridFormatterService,
		public groupService: GroupService, public loader: LoaderService, private subHeaderService: SubHeaderService) {
        this._formatter.applyFilterKeyboardHintTooltips(this.columnDefs);
	}

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        this.loadData();          
    }

    ngOnDestroy() {
        this.sub.forEach(s => { if (s) s.unsubscribe(); });
    }  

    uniqueLevelNum(): void {    
        let levelVal = this.levelConfigForm.controls['level'].value;
         
        this.uniqueLevel = this.coLevels.findIndex(x => x.levelNumber == levelVal) == -1 ? true : false;
    }

    deleteLevel($event: any): void {       
        var id = $event.data.levelId;           

        this.gridApi.forEachNode((node) => {
            if (node.data.levelId === id) {                
                this.removeLevel(id, node.data.categoryCode);
                var newLevels = this.coLevels.filter(x => x.levelId != id);
                this.coLevels = newLevels;
                this.gridApi.refreshCells();
                this.uniqueLevel = false;
            }
           
        })
    }
  
    getData(): void {
        this.loader.toggleLoader(true);
        let code: string = this.levelConfigForm.controls['category'].value;       

        var obj1$ = this.srv.getCoLevelsByCategory(code)
            .pipe(
                finalize(() => {
                    this.sub.push(obj1$);
                    this.loader.toggleLoader(false);
                })
            )
            .subscribe({
                next: (data) => {
                    if (data.length > 0) { this.coLevels = data; }
                },
                error: (err) => { 
                    console.error(err);
                }
            }
        );

        //load 10 numbers in number dropdownlist
        if (this.levelNumbers.length == 0) {
            for (var i = 1; i <= 10; i++) {
                this.levelNumbers.push(i);
            }
        }             

    }

    loadData(): void {
        this.loader.toggleLoader(true);
        var obj2$ = this.domainService.get('ChiefsOrderCategory')
            .pipe(
                finalize(() => {
                    this.sub.push(obj2$);
                    this.loader.toggleLoader(false);
                })
            )
            .subscribe({
                next: (data: LunaDomain) => {
                    this.activeCategories = (data.values as DomainValue[]);
                }, 
                error: err => { 
                    console.error(err);
                }
            });
                     
        let groups = this.groupService.get({ stubs: false }) as Observable<Group[]>;
        var obj3$ = groups
            .pipe(
                finalize(() => {
                    this.sub.push(obj3$);
                })
            )
            .subscribe({
                next: (res: any) => {
                    this.activeGroups = res;
                },
                error: err => { console.error(err); }
            }
           );
      }    

    removeLevel(id:string, categoryCode:string): void {

        var obj5$ = this.srv.deleteCoLevelRecord(id,categoryCode)
            .pipe(
                finalize(() => {
                    this.sub.push(obj5$);                   
                })
            )
            .subscribe({
                next: result => {
                
                }, 
                error: err => { console.error(err); }
            });

    }


    addLevel(): void {
       
        var rtnId = this.levelConfigForm.controls['returnTo'].value == null ? '' : this.levelConfigForm.controls['returnTo'].value;  
        var rtnName: string[] = [];
        var assignId: string = '';
        var assignGp: string = '';

        if (rtnId.length > 0) {
            rtnId.forEach((value: any) => {
                var val = this.activeGroups.find(x => x.id === value);
                if (val != null) {
                    rtnName.push(val.displayName);
                }
            });
        }

        //If they haven't chosen to select who to send the review to at run time, pull the group
        if (!this.levelConfigForm.controls['canSelectAssignToGroup'].value) {
            assignId = this.levelConfigForm.controls['assignTo'].value;
            assignGp = this.activeGroups.find(x => x.id === assignId).displayName;
        }

        const newLevel: chiefsOrderLevel = {
            categoryCode: this.levelConfigForm.controls['category'].value,
            levelNumber: this.levelConfigForm.controls['level'].value,
            assignToGroupId: assignId.toString(),
            assignToGroup:assignGp,
            returnToGroupId: rtnId.toString(),
            returnToGroup: rtnName.toString(),
            notification: this.levelConfigForm.controls['notification'].value,
            mode: this.levelConfigForm.controls['mode'].value,
            isLevelActive:true,
            canReject: this.levelConfigForm.controls['canReject'].value,
            canSelectAssignToGroup: this.levelConfigForm.controls['canSelectAssignToGroup'].value
        };

                    
  var obj4$ = this.srv.saveCoLevelRecord(newLevel)
            .pipe(
                finalize(() => {
                    this.sub.push(obj4$);
                })
            )
            .subscribe({
                next: result => {
                    if (result) {
                        newLevel.levelId = result.levelId;
                        this.coLevels.push(newLevel);            
                        this.gridApi.setGridOption("rowData", this.coLevels);
                        this.resetForm();
                        this.uniqueLevel = false;
                    }
                }, 
                error: err => { console.error(err);  }
            });
        
    }

    canCancelFormatter(component: any) {     
        return (obj: any) => this.getBooleanToYesOrNo(obj.data.canReject);
    }

    canAssignGroupAtReview(component: any) {
        return (obj: any) => this.getBooleanToYesOrNo(obj.data.canSelectAssignToGroup);
    }

    getBooleanToYesOrNo(bit: boolean): string {
        return bit ? 'Yes' : 'No';
    }

    isFormValid(): boolean {
        var isValid = (this.levelConfigForm.controls['assignTo'].value != '' || this.levelConfigForm.controls['canSelectAssignToGroup'].value == true)
                        && this.uniqueLevel;
        return isValid;
    }

    toggleHideGroupAssignment() {
        this.hideGroupAssignment = !this.hideGroupAssignment;
    }
    resetForm(): void {
        //only reset all expect category drop down.
        this.levelConfigForm.controls['level'].reset();
        this.levelConfigForm.controls['returnTo'].reset();
        this.levelConfigForm.controls['assignTo'].reset();
        this.levelConfigForm.controls['notification'].reset();
        this.levelConfigForm.controls['mode'].reset();
        this.levelConfigForm.controls['canReject'].reset();
        this.levelConfigForm.controls['canSelectAssignToGroup'].reset();
    }
 }

       



